import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../common/LanguageToggle';
import OperisLogo from '../common/OperisLogo';
import { useSignalR } from '../../context/SignalRContext';
import QRCodeCanvas from '../common/QRCode';
import AnimatedQRCode from '../common/AnimatedQRCode';

import {
  Bell,
  Search,
  User,
  LogOut,
  Settings,
  ChevronDown,
  X,
  Sun,
  Moon,
  Check,
  AlertCircle,
  Info,
  Clock,
  Trash2,
  CheckCheck,
  BellRing,
  Mail,
  MailOpen,
  MoreVertical,
  Timer,
  StickyNote,
  Palette,
} from 'lucide-react';

// Import notification API
import {
  getUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deactivateNotification,
  clearAllNotifications,
  getUnreadNotificationsCount
} from '../../api/notification.api';
import GlobalStickyNotes from '../common/GlobalStickyNotes';
import ThemeSettingsPanel from '../settings/ThemeSettingsPanel';
import { useTimer } from '../../context/TimerContext';

const Navbar = ({ onMenuClick, sidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRunningTimer, runningEntries: timerEntries = [] } = useTimer() || {};
  const notificationRef = useRef(null);

  // Hide the running-timer badge on the Time Entries page and auth pages
  const isTimeEntriesPage = location.pathname.startsWith('/time-entries');
  const isAuthPage = ['/login', '/register', '/'].includes(location.pathname);
  const showTimerBadge = hasRunningTimer && !isTimeEntriesPage && !isAuthPage;

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showGreenDot, setShowGreenDot] = useState(false);
  const [showStickyNotes, setShowStickyNotes] = useState(false);
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState(null);
  const { connection, isConnected } = useSignalR();

  // Extract order ID from notification message
  const extractOrderId = (message) => {
    if (!message) return null;

    // SAP / notification text: "OrderId:", legacy typo "Orderld:", or "order id"
    const orderIdPattern = /Order(?:Id|ld)\s*:\s*(\d+)/i;
    const match = message.match(orderIdPattern);

    if (match && match[1]) {
      return match[1];
    }

    // Alternative pattern for other formats
    const altPattern = /order[_\s]?id[:\s]*(\d+)/i;
    const altMatch = message.match(altPattern);

    return altMatch ? altMatch[1] : null;
  };

  const extractProductionLotId = (message) => {
    if (!message) return null;
    const match = message.match(/(?:LotId|Lot ID|Production Lot(?: ID)?)\s*:\s*([A-Za-z0-9_-]+)/i);
    return match?.[1] || null;
  };

  const extractProductionTaskId = (message) => {
    if (!message) return null;
    const match = message.match(/(?:TaskId|Task ID|Production Task(?: ID)?)\s*:\s*([A-Za-z0-9_-]+)/i);
    return match?.[1] || null;
  };

  const normalizeNotificationValue = (value) => {
    const normalized = value === null || value === undefined ? '' : String(value).trim();
    return normalized || null;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Show toast notification
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Handle QR code click
  const handleQRClick = (orderId, event) => {
    event.stopPropagation();
    setSelectedQRCode(orderId);
  };

  // Fetch unread count only (lightweight)
  const fetchUnreadCountOnly = useCallback(async () => {
    try {
      const count = await getUnreadNotificationsCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  // Load full notifications (heavy) - only when dropdown opens
  const loadFullNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getUserNotifications();

      let notificationData = [];

      if (Array.isArray(result)) {
        notificationData = result;
      } else if (result?.data && Array.isArray(result.data)) {
        notificationData = result.data;
      } else if (result?.items && Array.isArray(result.items)) {
        notificationData = result.items;
      } else {
        console.warn('⚠️ Unexpected notification structure:', result);
      }

      setNotifications(notificationData);
      // Update count from full data to ensure accuracy
      const unread = notificationData.filter(n => !n.isRead).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('❌ Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle new notification from SignalR
  const handleNewNotification = useCallback((notification) => {
    // Format the notification to match your existing structure
    const formattedNotification = {
      id: notification.id || notification.Id,
      title: notification.title || notification.Title || 'Task Assigned',
      message: notification.message || notification.Message,
      type: 'task_assigned',
      productionLotId: notification.productionLotId || notification.ProductionLotId,
      productionOrderId: notification.productionOrderId || notification.ProductionOrderId,
      productionTaskId: notification.productionTaskId || notification.ProductionTaskId,
      createdAt: notification.createdAt || notification.CreatedAt || new Date().toISOString(),
      isRead: notification.isRead || notification.IsRead || false
    };

    // Add to notifications list only if dropdown is open (to keep list updated)
    setNotifications(prev => {
      const exists = prev.some(n => n.id === formattedNotification.id);
      if (exists) return prev;
      return [formattedNotification, ...prev];
    });

    // Fetch accurate unread count from server
    fetchUnreadCountOnly();

    // Show green dot animation
    setShowGreenDot(true);
    setTimeout(() => setShowGreenDot(false), 3000);

    // Show toast notification
    // showToast(formattedNotification.message, 'info');

  }, [fetchUnreadCountOnly, showToast]);

  // Set up SignalR event listeners
  useEffect(() => {
    if (!connection) {
      return;
    }

    // Listen for new notifications
    connection.on('ReceiveNewNotification', handleNewNotification);

    // Listen for pong (test)
    connection.on('Pong', (message) => {
    });

    // Clean up event listeners
    return () => {
      connection.off('ReceiveNewNotification', handleNewNotification);
      connection.off('Pong');
    };
  }, [connection, handleNewNotification]);

  // Load initial unread count on component mount (lightweight)
  useEffect(() => {
    if (user) {
      fetchUnreadCountOnly();
    }
  }, [user, fetchUnreadCountOnly]);

  // Fallback polling - only when SignalR is disconnected and using lightweight endpoint
  useEffect(() => {
    if (!user) return;

    // If SignalR is disconnected, poll occasionally as fallback
    let fallbackInterval;
    if (!isConnected) {
      fallbackInterval = setInterval(() => {
        fetchUnreadCountOnly(); // Use lightweight endpoint
      }, 30000); // Every 30 seconds as fallback
    }

    return () => {
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, [user, isConnected, fetchUnreadCountOnly]);

  // Show connection status in console
  useEffect(() => {
    if (isConnected) {
      console.log('✅ SignalR connected - real-time notifications active');
    } else {
      console.log('❌ SignalR disconnected - real-time notifications inactive');
    }
  }, [isConnected]);

  // Load full notifications only when dropdown opens
  useEffect(() => {
    if (showNotifications) {
      loadFullNotifications();
    }
  }, [showNotifications, loadFullNotifications]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format relative time (using client local time)
  const formatRelativeTime = (dateString) => {
    if (!dateString) return t('common.justNow');

    try {
      let dateStr = dateString;
      if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
        dateStr += 'Z';
      }
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return t('common.justNow');
      if (diffMins < 60) return t('time.minsAgo', { count: diffMins });
      if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
      if (diffDays === 1) return t('common.yesterday');
      if (diffDays < 7) return t('time.daysAgo', { count: diffDays });
      return date.toLocaleDateString();
    } catch (error) {
      return t('common.unknownTime');
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type, isRead) => {
    if (isRead) {
      return <MailOpen className="w-5 h-5 text-gray-400 dark:text-gray-500" />;
    }

    switch (type?.toLowerCase()) {
      case 'taskassigned':
      case 'task_assigned':
        return <Info className="w-5 h-5 text-blue-500 dark:text-white" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-amber-500 dark:text-amber-300" />;
      case 'success':
        return <Check className="w-5 h-5 text-emerald-500 dark:text-emerald-300" />;
      default:
        return <Mail className="w-5 h-5 text-operis-blue dark:text-white" />;
    }
  };

  // Handle mark as read
  const handleMarkAsRead = async (notificationId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    try {
      const result = await markNotificationAsRead(notificationId);
      if (result?.isSuccess || result?.success || result) {
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        );
        // Fetch updated count from server
        fetchUnreadCountOnly();
        showToast(t('navbar.notificationMarkedRead'));
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
      showToast(t('navbar.failedMarkRead'), 'error');
    }
  };

  // Handle delete single notification
  const handleDeleteNotification = async (notificationId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    try {
      const result = await deactivateNotification(notificationId);
      if (result?.isSuccess) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Fetch updated count from server
        fetchUnreadCountOnly();
        showToast(t('navbar.notificationDeleted'));
      }
    } catch (error) {
      showToast(t('navbar.failedDeleteNotification'), 'error');
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    try {
      const result = await markAllNotificationsAsRead();
      if (result?.isSuccess) {
        setNotifications(prev =>
          prev.map(n => ({ ...n, isRead: true }))
        );
        // Fetch updated count from server
        fetchUnreadCountOnly();
        showToast(t('navbar.allNotificationsMarkedRead'));
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      showToast(t('navbar.failedMarkAllRead'), 'error');
    }
  };

  // Handle clear all notifications
  const handleClearAll = async (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    try {
      const result = await clearAllNotifications();
      setNotifications([]);
      // Fetch updated count from server
      fetchUnreadCountOnly();
      showToast(t('navbar.allNotificationsCleared'));
    } catch (error) {
      showToast(t('navbar.failedClearNotifications'), 'error');
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    try {
      if (!notification.isRead && notification.id) {
        await markNotificationAsRead(notification.id);

        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, isRead: true } : n
          )
        );
        // Fetch updated count from server
        fetchUnreadCountOnly();
      }

      setShowNotifications(false);

      const productionLotId =
        normalizeNotificationValue(notification.productionLotId) ||
        extractProductionLotId(notification.message);
      const productionTaskId =
        normalizeNotificationValue(notification.productionTaskId) ||
        extractProductionTaskId(notification.message);
      const productionOrderId =
        normalizeNotificationValue(notification.productionOrderId) ||
        extractOrderId(notification.message);

      if (productionLotId || productionTaskId) {
        const params = new URLSearchParams({ openTask: 'true' });
        if (productionLotId) params.set('productionLotId', productionLotId);
        if (productionTaskId) params.set('productionTaskId', productionTaskId);
        if (productionOrderId) params.set('productionOrderId', productionOrderId);
        navigate(`/time-entries?${params.toString()}`);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      showToast(t('navbar.errorProcessingNotification'), 'error');
    }
  };

  return (
    <>
      <nav className="sticky top-0 z-40 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">

        {/* Toast Notification */}
        {toast && (
          <div className={`fixed top-16 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg animate-slide-up ${toast.type === 'error' ? 'bg-red-600 text-white' :
            toast.type === 'info' ? 'bg-blue-600 text-white' :
              'bg-emerald-600 text-white'
            }`}>
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> :
              toast.type === 'info' ? <Info className="w-5 h-5" /> :
                <Check className="w-5 h-5" />}
            <span className="font-medium">{toast.message}</span>
          </div>
        )}

        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side */}
            <div className="flex items-center gap-3">
              {/* Hamburger / sidebar toggle — visible on all screen sizes */}
              <button
                onClick={onMenuClick}
                title={sidebarCollapsed ? t('navbar.expandSidebar') : t('navbar.collapseSidebar')}
                className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 dark:text-gray-400 hover:text-operis-blue dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-all duration-200 group"
                aria-label={t('navbar.toggleSidebar')}
              >
                {/* Custom three-bar icon that morphs based on collapsed state */}
                <span className="flex flex-col gap-[5px] w-[18px]">
                  <span className={`block h-[2px] rounded-full bg-current transition-all duration-300 ${sidebarCollapsed ? 'w-full' : 'w-full'}`} />
                  <span className={`block h-[2px] rounded-full bg-current transition-all duration-300 ${sidebarCollapsed ? 'w-3/4' : 'w-full'}`} />
                  <span className={`block h-[2px] rounded-full bg-current transition-all duration-300 ${sidebarCollapsed ? 'w-1/2' : 'w-full'}`} />
                </span>
              </button>

              <Link to="/dashboard" className="flex items-center">
                <OperisLogo size="sm" showTagline />
              </Link>
            </div>

            {/* Search - Desktop */}
            {/* <div className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search time entries, tasks..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-operis-blue focus:bg-white dark:focus:bg-gray-700 transition-all dark:text-gray-200 dark:placeholder-gray-400"
                />
              </div>
            </div> */}

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Running Timer Badge — visible on all pages except Time Entries & auth */}
              {showTimerBadge && (
                <div className="relative group">
                  <button
                    onClick={() => navigate('/time-entries')}
                    className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-500/10 hover:bg-teal-500/20 dark:bg-teal-400/10 dark:hover:bg-teal-400/20 text-teal-600 dark:text-teal-400 transition-all"
                  >
                    {/* Ping ring */}
                    <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-teal-500" />
                    </span>
                    <Timer className="w-4 h-4 animate-pulse" />
                    <span className="hidden sm:inline text-xs font-semibold tracking-wide">{t('navbar.timerRunning')}</span>
                  </button>

                  {/* Hover tooltip — shows each running task's ID & description */}
                  <div className="pointer-events-none absolute right-0 top-full mt-2 z-50 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-200">
                    <div className="bg-white dark:bg-gray-800 border border-teal-200 dark:border-teal-700 rounded-2xl shadow-xl min-w-[220px] max-w-[300px] overflow-hidden">
                      {/* Header */}
                      <div className="px-4 py-2.5 bg-teal-500/10 dark:bg-teal-400/10 border-b border-teal-200 dark:border-teal-700 flex items-center gap-2">
                        <Timer className="w-3.5 h-3.5 text-teal-500 dark:text-teal-400 animate-pulse" />
                        <span className="text-xs font-bold text-teal-700 dark:text-teal-300 tracking-wide uppercase">
                          {timerEntries.length === 1
                            ? t('navbar.activeTimer')
                            : t('navbar.activeTimers', { count: timerEntries.length })}
                        </span>
                      </div>
                      {/* Task rows */}
                      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                        {timerEntries.map((entry) => {
                          const taskId = entry.productionTaskId || entry.referenceProductionTaskId || entry.id;
                          return (
                            <li key={entry.id} className="px-4 py-2.5 flex items-start gap-2">
                              <span className="mt-0.5 flex-shrink-0 w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">
                                  {taskId}
                                </p>
                                {entry.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                    {entry.description}
                                  </p>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                      {/* Footer hint */}

                    </div>
                    {/* Arrow */}
                    <div className="absolute -top-1.5 right-4 w-3 h-3 rotate-45 bg-white dark:bg-gray-800 border-l border-t border-teal-200 dark:border-teal-700" />
                  </div>
                </div>
              )}

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                title={darkMode ? t('navbar.switchToLight') : t('navbar.switchToDark')}
              >
                {darkMode ? <Sun className="w-5 h-5 text-white fill-white" /> : <Moon className="w-5 h-5 text-black stroke-2" />}
              </button>

              <LanguageToggle />

              {/* Theme Settings */}
              <button
                onClick={() => setShowThemePanel(true)}
                className="relative p-2 rounded-xl transition-all duration-300 group overflow-hidden bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                title={t('navbar.appearanceSettings')}
              >
                <Palette className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" />
              </button>

              {/* Sticky Notes */}
              <button
                onClick={() => setShowStickyNotes(!showStickyNotes)}
                className={`relative p-2 rounded-xl transition-colors ${showStickyNotes
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                title={t('navbar.stickyNotes')}
              >
                <StickyNote className="w-5 h-5" />
              </button>

              {/* Notifications */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <Bell className="w-6 h-6" />

                  {/* Unread count badge */}
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex items-center justify-center min-w-[1.25rem] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full border-2 border-white dark:border-gray-900">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}

                  {/* Green dot animation for new notifications */}
                  {showGreenDot && (
                    <>
                      <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900 animate-ping" />
                      <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900" />
                    </>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-20 overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-operis-blue/5 to-primary-500/5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <BellRing className="w-5 h-5 text-operis-blue dark:text-white" />
                          <h3 className="font-semibold text-gray-900 dark:text-white">{t('navbar.notifications')}</h3>
                          {unreadCount > 0 && (
                            <span className="px-2 py-0.5 text-xs bg-operis-blue dark:bg-indigo-500 text-white rounded-full">
                              {t('navbar.newCount', { count: unreadCount })}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {unreadCount > 0 && (
                            <button
                              onClick={(event) => handleMarkAllAsRead(event)}
                              className="p-2 text-operis-blue dark:text-gray-300 hover:bg-operis-blue/10 dark:hover:bg-gray-600 rounded-lg transition-colors"
                              title={t('navbar.markAllAsRead')}
                            >
                              <CheckCheck className="w-4 h-4" />
                            </button>
                          )}
                          {notifications.length > 0 && (
                            <button
                              onClick={(event) => handleClearAll(event)}
                              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                              title={t('navbar.clearAllNotifications')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Notifications List */}
                    <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[32rem] overflow-y-auto">
                      {loading ? (
                        <div className="p-8 text-center">
                          <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-operis-blue rounded-full animate-spin"></div>
                          <p className="mt-2 text-sm text-gray-500">{t('navbar.loadingNotifications')}</p>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-12 text-center">
                          <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                            <Bell className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-gray-500 dark:text-gray-400 font-medium">{t('navbar.noNotifications')}</p>
                          <p className="text-xs text-gray-400 mt-1">{t('navbar.allCaughtUp')}</p>
                        </div>
                      ) : (
                        notifications.map((notif) => {
                          const orderId = extractOrderId(notif.message);

                          return (
                            <div
                              key={notif.id}
                              className={`relative group transition-all ${!notif.isRead
                                ? 'bg-gradient-to-r from-blue-50/80 to-indigo-50/50 dark:from-blue-900/20 dark:to-indigo-900/10'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                }`}
                            >
                              <div
                                onClick={(event) => handleNotificationClick(notif, event)}
                                className="p-4 cursor-pointer"
                              >
                                <div className="flex gap-3">
                                  {/* Notification Icon */}
                                  <div className="flex-shrink-0 mt-1">
                                    {getNotificationIcon(notif.type, notif.isRead)}
                                  </div>

                                  {/* Notification Content */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <p className={`text-sm ${!notif.isRead
                                        ? 'font-semibold text-gray-900 dark:text-white'
                                        : 'text-gray-600 dark:text-gray-300'
                                        }`}>
                                        {notif.message && notif.message.split(/(\d+)/g).map((part, index) => {
                                          if (part.match(/^\d+$/)) {
                                            return (
                                              <span key={index} className="font-extrabold text-operis-blue dark:text-white">
                                                {part}
                                              </span>
                                            );
                                          }
                                          return part;
                                        })}
                                      </p>

                                      {/* Action Buttons */}
                                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {!notif.isRead && notif.id && (
                                          <button
                                            onClick={(event) => handleMarkAsRead(notif.id, event)}
                                            className="p-1 text-operis-blue dark:text-gray-300 hover:bg-operis-blue/10 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                            title={t('navbar.markAsRead')}
                                          >
                                            <CheckCheck className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                        <button
                                          onClick={(event) => handleDeleteNotification(notif.id, event)}
                                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                                          title={t('navbar.delete')}
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Metadata Row */}
                                    <div className="flex items-center gap-2 mt-1">
                                      <p className="text-xs text-gray-400 dark:text-gray-400">
                                        {formatRelativeTime(notif.createdAt)}
                                      </p>
                                      <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${!notif.isRead
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                        }`}>
                                        {!notif.isRead ? t('navbar.unread') : t('navbar.read')}
                                      </span>
                                    </div>
                                  </div>

                                  {/* QR Code Section - Only show if orderId exists */}
                                  {orderId && (
                                    <div className="flex-shrink-0 ml-2">
                                      <div
                                        className="relative group/qr cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleQRClick(orderId, e);
                                        }}
                                      >
                                        {/* Use static QR code in list */}
                                        <QRCodeCanvas
                                          value={`OrderId: ${orderId}`}
                                          size={40}
                                          className="rounded-lg transition-transform duration-200 group-hover/qr:scale-110"
                                        />

                                        {/* Tooltip */}
                                        <div className="absolute right-0 bottom-full mb-2 hidden group-hover/qr:block z-50">
                                          <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                            {t('navbar.clickToEnlarge')}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="p-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                        <button
                          onClick={() => setShowNotifications(false)}
                          className="block w-full text-center text-sm text-operis-blue dark:text-gray-300 hover:text-primary-700 dark:hover:text-white font-medium py-1"
                        >
                          {t('common.close')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-operis-blue to-primary-700 rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white font-semibold text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email || ''}</p>
                  </div>
                  <ChevronDown className="hidden md:block w-4 h-4 text-gray-400" />
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowUserMenu(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 z-20 overflow-hidden">
                      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{user?.name || 'User'}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email || ''}</p>
                      </div>
                      <div className="p-2">
                        <Link
                          to="/profile"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <User className="w-4 h-4" />
                          {t('navbar.profile')}
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          {t('navbar.settings')}
                        </Link>
                      </div>
                      <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('navbar.signOut')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* QR Code Modal */}
      {selectedQRCode && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setSelectedQRCode(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('navbar.orderId', { id: selectedQRCode })}
              </h3>
              <button
                onClick={() => setSelectedQRCode(null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex justify-center p-4">
              <AnimatedQRCode
                value={`OrderId: ${selectedQRCode}`}
                size={200}
                onComplete={() => console.log('Animation complete!')}
              />
            </div>
          </div>
        </div>
      )}

      {/* Global Sticky Notes */}
      <GlobalStickyNotes isOpen={showStickyNotes} onClose={() => setShowStickyNotes(false)} />

      {/* Theme Settings Panel */}
      <ThemeSettingsPanel isOpen={showThemePanel} onClose={() => setShowThemePanel(false)} />
    </>
  );
};

export default Navbar;
